import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowForwardRounded, Fingerprint, QrCode2Outlined } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import * as cbor from 'cbor-web';
import { Buffer } from 'buffer';
import { QRCodeSVG } from 'qrcode.react';
import './DigitalIdApp.css';
import {
  BackButton,
  EnterpriseButton,
  EnterpriseCard,
  EnterpriseGrid,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar
} from './ui/EnterpriseLayout';

// Configure Buffer for the browser environment
window.Buffer = window.Buffer || Buffer;

const environment = {
  apiUrl: 'https://verifier.veritage.io/ui/presentations'
};

const AGE_ATTESTATION = 18; // Hardcoded to 18

// Utility function to detect mobile browsers
function isMobileBrowser() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return (/android|iphone|ipad|ipod|opera mini|iemobile|mobile/i).test(ua);
}

function DigitalIdApp() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrValue, setQrValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to process vp_token and verify age
  const processVpToken = useCallback(async (vpToken, requiredAge) => {
    try {
      console.log('Raw vp_token:', vpToken);
      
      // Decode CBOR token using Buffer and proper options
      const buf = Buffer.from(vpToken, 'base64');
      
      // Decode the CBOR data
      const valueOut = await cbor.decodeFirst(buf, {
        bigint: true,
        preferWeb: true
      });
      
      console.log('Decoded CBOR token:', valueOut);
      
      // Extract the namespaces and data according to the sample code approach
      if (valueOut.documents && valueOut.documents[0] && valueOut.documents[0].issuerSigned) {
        const namespaces = valueOut.documents[0].issuerSigned.nameSpaces;
        const namespaceKeys = Object.keys(namespaces);
        
        if (namespaceKeys.length > 0) {
          const firstNamespace = namespaceKeys[0];
          const dataArray = namespaces[firstNamespace];
          
          console.log('First namespace:', firstNamespace);
          console.log('Data array:', dataArray);
          
          // Process each item in the data array
          const extractedData = [];
          
          for (const item of dataArray) {
            if (item.value) {
              try {
                // Decode the inner CBOR data
                const decodedItem = await cbor.decodeFirst(item.value, {
                  bigint: true,
                  preferWeb: true
                });
                
                console.log('Decoded item:', decodedItem);
                
                if (decodedItem.elementIdentifier && decodedItem.elementValue) {
                  extractedData.push({
                    key: decodedItem.elementIdentifier.replaceAll('_', ' '),
                    value: decodedItem.elementValue
                  });
                }
              } catch (itemError) {
                console.error('Error decoding item:', itemError);
              }
            }
          }
          
          console.log('Extracted data:', extractedData);
          
          // Update UI based on extracted data
          if (extractedData.length > 0) {
            // Check if age verification passes based on birth date
            const birthDateItem = extractedData.find(item => item.key.includes('birth date'));
            if (birthDateItem) {
              console.log(`Found birth date: ${birthDateItem.value}`);
              
              // Extract the date string from CBOR tag if needed
              let birthDateStr;
              
              if (typeof birthDateItem.value === 'object' && birthDateItem.value !== null) {
                // Handle CBOR tagged value (e.g., 1004("1992-08-08"))
                if (birthDateItem.value.value && typeof birthDateItem.value.value === 'string') {
                  birthDateStr = birthDateItem.value.value;
                } else if (birthDateItem.value.toString) {
                  // Try to extract date from the string representation
                  const tagStr = birthDateItem.value.toString();
                  const dateMatch = tagStr.match(/"([0-9-]+)"/);
                  if (dateMatch && dateMatch[1]) {
                    birthDateStr = dateMatch[1];
                  }
                }
                console.log(`Extracted date string from CBOR tag: ${birthDateStr}`);
              } else if (typeof birthDateItem.value === 'string') {
                // Direct string value
                birthDateStr = birthDateItem.value;
              }
              
              if (!birthDateStr) {
                console.error('Could not extract valid birth date string');
                return false;
              }
              
              // Calculate age based on birth date
              const birthDate = new Date(birthDateStr);
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
              
              console.log(`Calculated age: ${age}, Required age: ${requiredAge}`);
              
              if (age >= requiredAge) {
                console.log('Age verification passed!');
                setAuthenticated(true);
                // Navigate to result screen after a brief delay
                setTimeout(() => {
                  navigate('/age-verified', {
                    state: {
                      isDigitalId: true,
                      isVerified: true,
                      age: age
                    }
                  });
                }, 500);
                return true;
              } else {
                console.log('Age verification failed - underage');
                setAuthenticated(false);
                // Navigate to result screen with failure
                setTimeout(() => {
                  navigate('/age-verified', {
                    state: {
                      isDigitalId: true,
                      isVerified: false,
                      age: age,
                      requiredAge: requiredAge
                    }
                  });
                }, 500);
                return false;
              }
            }
          }
        } else {
          console.warn('No namespaces found in the token');
        }
      } else {
        console.warn('Token does not contain the expected structure');
      }
    } catch (error) {
      console.error('Error decoding CBOR:', error);
      // Try fallback approach with direct parsing
      try {
        if (vpToken.includes('birth_date')) {
          console.log('Attempting fallback parsing of birth date');
          // Simple fallback to detect if it appears to be an adult
          if (vpToken.includes('1990') || 
              vpToken.includes('1980') || 
              vpToken.includes('1970')) {
            console.log('Fallback detected adult birth year');
            setAuthenticated(true);
            setTimeout(() => {
              navigate('/age-verified', {
                state: {
                  isDigitalId: true,
                  isVerified: true
                }
              });
            }, 500);
            return true;
          }
        }
      } catch (fallbackError) {
        console.error('Fallback parsing failed:', fallbackError);
      }
    }
    return false;
  }, [navigate]);

  const handleFingerprintClick = async () => {
    console.log("clicked");
    
    setIsLoading(true);

    const nonce = uuidv4();
    const presentationId = uuidv4();

    const requestBody = {
      type: "vp_token",
      presentation_definition: {
        id: presentationId,
        "input_descriptors": [
          {
            "id": "eu.europa.ec.eudi.pid.1",
            "name": "Person Identification Data (PID)",
            "purpose": "",
            "format": {
              "mso_mdoc": {
                "alg": [
                  "ES256",
                  "ES384",
                  "ES512"
                ]
              }
            },
            "constraints": {
              "fields": [
                {
                  "path": [
                    "$['eu.europa.ec.eudi.pid.1']['birth_date']"
                  ],
                  "intent_to_retain": false
                }
              ]
            }
          }
        ]
      },
      nonce: nonce,
      ...(isMobileBrowser() && {
        wallet_response_redirect_uri_template: "https://demo.veritage.io/get-wallet-code?response_code={RESPONSE_CODE}"
      })
    };

    try {
      const response = await fetch(environment.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const presentation = await response.json();
        
        // Store presentation with age attestation in local storage
        localStorage.setItem('presentation', JSON.stringify({
          ...presentation,
          ageAttestation: AGE_ATTESTATION
        }));

        // Build the deeplink/QR URL
        const requestUri = encodeURIComponent(presentation.request_uri);
        const qrUrl = `eudi-openid4vp://verifier.veritage.io/ui/presentations?client_id=wallet-dev&request_uri=${requestUri}`;
        
        if (isMobileBrowser()) {
          // Open the deeplink
          window.location.href = qrUrl;
        } else {
          // Show QR code
          setQrValue(qrUrl);
          setShowQr(true);
        }
        setIsLoading(false);
      } else {
        console.error('Failed to send request', response.statusText);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error sending request', error);
      setIsLoading(false);
    }
  };

  // Poll for presentation when QR is shown (non-mobile)
  useEffect(() => {
    if (!showQr) {
      return; // Don't poll if QR is not shown
    }

    const storedPresentationStr = localStorage.getItem('presentation');
    if (!storedPresentationStr) {
      console.error('No presentation found in local storage');
      return;
    }

    const storedPresentation = JSON.parse(storedPresentationStr);
    const presentationId = storedPresentation.presentation_id;
    const storedAgeAttestation = AGE_ATTESTATION;

    console.log(`Starting polling for presentation: ${presentationId}`);
    
    const pollInterval = 2000; // Poll every 2 seconds
    let pollCount = 0;
    const maxPolls = 150; // Max 5 minutes (150 * 2 seconds)
    let intervalId = null;
    let isPolling = true;
    
    const stopPolling = () => {
      isPolling = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    
    const pollPresentation = async () => {
      if (!isPolling) {
        return;
      }

      try {
        const getRequestUrl = `${environment.apiUrl}/${presentationId}`;
        const response = await fetch(getRequestUrl);
        
        if (response.ok) {
          const result = await response.json();
          console.log('Polling - Verification result:', result);
          
          // Check if vp_token exists in the response
          if (result.vp_token) {
            console.log('Polling - vp_token received, processing...');
            stopPolling();
            await processVpToken(result.vp_token, storedAgeAttestation);
            // Stop polling and close QR after processing
            setShowQr(false);
            return;
          } else {
            console.log('Polling - No vp_token yet, continuing...');
          }
        } else if (response.status === 400) {
          // Expected 400 until ready, continue polling
          pollCount++;
          if (pollCount >= maxPolls) {
            console.error('Polling timeout - max polls reached');
            stopPolling();
            setShowQr(false);
            return;
          }
          console.log(`Polling - Still waiting (attempt ${pollCount}/${maxPolls})...`);
        } else {
          console.error('Polling - Failed to fetch presentation', response.status, response.statusText);
          // Continue polling on other errors
        }
      } catch (error) {
        console.error('Polling - Error fetching presentation', error);
        // Continue polling on error
      }
    };

    // Start polling immediately, then set up interval
    pollPresentation();
    
    intervalId = setInterval(pollPresentation, pollInterval);

    // Cleanup function
    return () => {
      stopPolling();
    };
  }, [showQr, navigate, processVpToken]);

  // Handle response_code from URL params (mobile redirect flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const responseCode = urlParams.get('response_code');

    if (responseCode) {
      const storedPresentationStr = localStorage.getItem('presentation');
      if (storedPresentationStr) {
        const storedPresentation = JSON.parse(storedPresentationStr);
        const presentationId = storedPresentation.presentation_id;
        const storedAgeAttestation = AGE_ATTESTATION;
        
        console.log(`Retrieving verification with age attestation: ${storedAgeAttestation}`);
        
        const getRequestUrl = `${environment.apiUrl}/${presentationId}?response_code=${responseCode}`;
        
        const fetchPresentation = async () => {
          try {
            const response = await fetch(getRequestUrl);
            if (response.ok) {
              const result = await response.json();
              console.log('Verification result:', result);
              
              // Check if vp_token exists in the response
              if (result.vp_token) {
                await processVpToken(result.vp_token, storedAgeAttestation);
              } else {
                console.warn('No vp_token found in the response');
              }
            } else {
              console.error('Failed to fetch presentation', response.statusText);
            }
          } catch (error) {
            console.error('Error fetching presentation', error);
          }
        };

        fetchPresentation();
      } else {
        console.error('No presentation found in local storage');
      }
    }
  }, [navigate, processVpToken]);

  return (
    <EnterpriseShell>
      <EnterpriseTopBar
        right={
          <>
            <BackButton onClick={() => navigate('/')} label="Back" />
            <span className="vt-chip vt-chip--subtle">Digital ID</span>
          </>
        }
      />

      <EnterpriseHero
        title="Verify with Digital ID"
        subtitle="Confirm you're 18+ by sharing an age attestation, and nothing more."
      />

      <EnterpriseGrid columns={1}>
        <EnterpriseCard>
          {!showQr && !authenticated && (
            <>
              <div className="vt-cardHeader">
                <div className="vt-iconTile" aria-hidden="true">
                  <Fingerprint className="vt-iconTileIcon" />
                </div>
                <div>
                  <h2 className="vt-cardTitle">Use your wallet</h2>
                  <p className="vt-cardDescription">
                    We’ll open your digital ID wallet app or show a QR code for a mobile device to scan.
                  </p>
                </div>
              </div>

              <div className="vt-actions" style={{ gridTemplateColumns: '1fr' }}>
                <EnterpriseButton variant="primary" onClick={handleFingerprintClick} disabled={isLoading}>
                  <span>{isLoading ? 'Loading…' : 'Verify with Digital ID'}</span>
                  <ArrowForwardRounded className="vt-btnIcon" />
                </EnterpriseButton>
              </div>
            </>
          )}

          {showQr && !authenticated && (
            <>
              <div className="vt-cardHeader">
                <div className="vt-iconTile" aria-hidden="true">
                  <QrCode2Outlined className="vt-iconTileIcon" />
                </div>
                <div>
                  <h2 className="vt-cardTitle">Scan the QR code</h2>
                  <p className="vt-cardDescription">Scan with your mobile wallet app.</p>
                </div>
              </div>

              <div className="qr-code-container">
                <div className="qr-code-wrapper">
                  <QRCodeSVG 
                    value={qrValue} 
                    size={320}
                    level="H"
                    fgColor="#000000"
                    bgColor="#FFFFFF"
                  />
                </div>
              </div>

              <div className="vt-actions" style={{ gridTemplateColumns: '1fr' }}>
                <EnterpriseButton variant="secondary" type="button" onClick={() => setShowQr(false)}>
                  Cancel
                </EnterpriseButton>
              </div>
            </>
          )}
        </EnterpriseCard>
      </EnterpriseGrid>
    </EnterpriseShell>
  );
}

export default DigitalIdApp;
